import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import ParticleCanvas from "./ParticleCanvas";

const Hero = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const spotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    const spot = spotRef.current;
    if (!el || !spot) return;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      spot.style.background = `radial-gradient(700px circle at ${x}px ${y}px,rgba(255,28,53,0.14) 0%,rgba(255,28,53,0.04) 40%,transparent 68%)`;
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={heroRef} style={{ position:"relative",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",padding:"0 24px",cursor:"crosshair" }}>
      <style>{`
        @keyframes glitchBase{0%,88%,100%{transform:translate(0);filter:none}89%{transform:translate(-4px,1px);filter:hue-rotate(80deg)}90%{transform:translate(4px,-2px)}91%{transform:translate(-2px,2px);filter:hue-rotate(-80deg)}92%{transform:translate(0);filter:none}}
        @keyframes glitchT{0%,87%,100%{clip-path:inset(0 0 100% 0);transform:translate(0)}88%{clip-path:inset(8% 0 72% 0);transform:translate(-6px,0);color:#ff6b6b}89%{clip-path:inset(55% 0 15% 0);transform:translate(5px,0)}90%{clip-path:inset(0 0 100% 0);transform:translate(0)}91%{clip-path:inset(30% 0 40% 0);transform:translate(-3px,0);color:#FF1C35}92%{clip-path:inset(0 0 100% 0)}}
        @keyframes glitchB{0%,86%,100%{clip-path:inset(100% 0 0 0);transform:translate(0)}87%{clip-path:inset(65% 0 8% 0);transform:translate(5px,0);color:#ff9999}88%{clip-path:inset(35% 0 38% 0);transform:translate(-4px,0)}89%{clip-path:inset(100% 0 0 0);transform:translate(0)}90%{clip-path:inset(15% 0 60% 0);transform:translate(3px,0)}91%{clip-path:inset(100% 0 0 0)}}
        @keyframes neonFlicker{0%,18%,20%,24%,52%,54%,100%{text-shadow:0 0 7px #FF1C35,0 0 25px #FF1C35,0 0 55px rgba(255,28,53,0.55),0 0 100px rgba(255,28,53,0.25)}19%,23%,53%{text-shadow:none;opacity:0.85}}
        @keyframes beamSweep{0%{left:-35%;opacity:0}8%{opacity:1}92%{opacity:0.7}100%{left:120%;opacity:0}}
        @keyframes hScan{0%{top:-1px;opacity:0}4%{opacity:1}96%{opacity:0.5}100%{top:100%;opacity:0}}
        @keyframes floatCard{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
        @keyframes pulseDot{0%,100%{box-shadow:0 0 0 0 rgba(255,28,53,0.8),0 0 6px rgba(255,28,53,0.6)}50%{box-shadow:0 0 0 7px rgba(255,28,53,0),0 0 18px rgba(255,28,53,0.9)}}
        @keyframes dataLineAnim{0%{transform:scaleX(0);transform-origin:left}50%{transform:scaleX(1);transform-origin:left}51%{transform:scaleX(1);transform-origin:right}100%{transform:scaleX(0);transform-origin:right}}
        @keyframes borderDraw{from{stroke-dashoffset:200}to{stroke-dashoffset:0}}
        .glitch-wrap{position:relative;display:inline-block;animation:glitchBase 7s infinite}
        .glitch-wrap::before,.glitch-wrap::after{content:attr(data-text);position:absolute;left:0;top:0;width:100%;color:#FF1C35}
        .glitch-wrap::before{animation:glitchT 7s infinite}
        .glitch-wrap::after{animation:glitchB 7s infinite}
        .neon{animation:neonFlicker 3.5s infinite}
        .beam{position:absolute;top:0;bottom:0;pointer-events:none;transform:skewX(-13deg)}
        .b1{width:280px;background:linear-gradient(90deg,transparent 0%,rgba(255,28,53,0.07) 35%,rgba(255,28,53,0.16) 50%,rgba(255,28,53,0.07) 65%,transparent 100%);animation:beamSweep 7.5s ease-in-out infinite}
        .b2{width:130px;background:linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.025) 50%,transparent 100%);animation:beamSweep 12s ease-in-out infinite;animation-delay:3s}
        .b3{width:70px;background:linear-gradient(90deg,transparent 0%,rgba(255,28,53,0.07) 50%,transparent 100%);animation:beamSweep 17s ease-in-out infinite;animation-delay:7s}
        .hscan{position:absolute;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent 0%,rgba(255,28,53,0.5) 28%,rgba(255,240,200,0.9) 50%,rgba(255,28,53,0.5) 72%,transparent 100%);pointer-events:none;animation:hScan linear infinite}
        .fcard{animation:floatCard ease-in-out infinite}
        .pdot{width:8px;height:8px;border-radius:50%;background:#FF1C35;flex-shrink:0;animation:pulseDot 1.7s infinite}
        .dline{height:1px;background:linear-gradient(90deg,#FF1C35,transparent);animation:dataLineAnim 3s ease-in-out infinite}
      `}</style>

      {/* Mouse spotlight */}
      <div ref={spotRef} style={{ position:"absolute",inset:0,zIndex:2,pointerEvents:"none",transition:"background 0.06s ease" }}/>

      {/* Center radial glow */}
      <div style={{ position:"absolute",inset:0,zIndex:1,background:"radial-gradient(ellipse 95% 60% at 50% 25%,rgba(255,28,53,0.16) 0%,transparent 68%)" }}/>

      {/* Particles */}
      <div style={{ position:"absolute",inset:0,zIndex:3 }}><ParticleCanvas opacity={0.95}/></div>

      {/* Perspective grid */}
      <div style={{ position:"absolute",bottom:0,left:0,right:0,height:"54%",zIndex:4,overflow:"hidden" }}>
        <svg width="100%" height="100%" viewBox="0 0 1440 480" preserveAspectRatio="none" style={{ position:"absolute",bottom:0 }}>
          <defs>
            <linearGradient id="hgl2" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="transparent"/>
              <stop offset="25%" stopColor="rgba(255,28,53,0.35)"/>
              <stop offset="50%" stopColor="rgba(255,210,210,0.8)"/>
              <stop offset="75%" stopColor="rgba(255,28,53,0.35)"/>
              <stop offset="100%" stopColor="transparent"/>
            </linearGradient>
          </defs>
          {[0.07,0.16,0.27,0.40,0.55,0.70,0.83,0.93,1].map((t,i)=>{
            const y=t*480; const xo=660*(1-t);
            return <line key={i} x1={xo} y1={y} x2={1440-xo} y2={y} stroke="rgba(255,28,53,0.15)" strokeWidth="0.9"/>;
          })}
          {Array.from({length:21},(_,i)=>{
            const f=i/20; const x0=720+(f-0.5)*48; const x1=f*1440;
            return <line key={i} x1={x0} y1={0} x2={x1} y2={480} stroke="rgba(255,28,53,0.09)" strokeWidth="0.7"/>;
          })}
          <line x1="0" y1="1" x2="1440" y2="1" stroke="url(#hgl2)" strokeWidth="2"/>
        </svg>
        <div style={{ position:"absolute",bottom:0,left:0,right:0,height:"58%",background:"linear-gradient(to top,#080808 0%,transparent 100%)" }}/>
      </div>

      {/* Light beams */}
      <div style={{ position:"absolute",inset:0,zIndex:5,overflow:"hidden" }}>
        <div className="beam b1"/><div className="beam b2"/><div className="beam b3"/>
      </div>

      {/* Horizontal scan lines */}
      <div style={{ position:"absolute",inset:0,zIndex:6,overflow:"hidden" }}>
        <div className="hscan" style={{ animationDuration:"5s",animationDelay:"0s" }}/>
        <div className="hscan" style={{ animationDuration:"8.5s",animationDelay:"2.8s",opacity:0.5 }}/>
        <div className="hscan" style={{ animationDuration:"13s",animationDelay:"6s",opacity:0.3 }}/>
      </div>

      {/* Vignette */}
      <div style={{ position:"absolute",inset:0,zIndex:7,background:"radial-gradient(ellipse 85% 85% at 50% 50%,transparent 35%,rgba(0,0,0,0.65) 100%)",pointerEvents:"none" }}/>

      {/* HUD brackets + floating cards */}
      <div style={{ position:"absolute",inset:0,zIndex:8,pointerEvents:"none" }}>
        <svg style={{ position:"absolute",top:78,left:28 }} width="64" height="64">
          <path d="M22,0 L0,0 L0,22" fill="none" stroke="rgba(255,28,53,0.75)" strokeWidth="1.5" strokeDasharray="200" style={{ animation:"borderDraw 1.5s ease forwards 0.5s",strokeDashoffset:200 }}/>
          <circle cx="0" cy="0" r="2.5" fill="#FF1C35"/>
        </svg>
        <svg style={{ position:"absolute",top:78,right:28 }} width="64" height="64">
          <path d="M42,0 L64,0 L64,22" fill="none" stroke="rgba(255,28,53,0.75)" strokeWidth="1.5" strokeDasharray="200" style={{ animation:"borderDraw 1.5s ease forwards 0.7s",strokeDashoffset:200 }}/>
          <circle cx="64" cy="0" r="2.5" fill="#FF1C35"/>
        </svg>
        <svg style={{ position:"absolute",bottom:28,left:28 }} width="64" height="64">
          <path d="M0,42 L0,64 L22,64" fill="none" stroke="rgba(255,28,53,0.3)" strokeWidth="1.5"/>
        </svg>
        <svg style={{ position:"absolute",bottom:28,right:28 }} width="64" height="64">
          <path d="M64,42 L64,64 L42,64" fill="none" stroke="rgba(255,28,53,0.3)" strokeWidth="1.5"/>
        </svg>
        {/* Left card */}
        <div className="fcard" style={{ position:"absolute",left:"4.5%",top:"41%",animationDuration:"5s" }}>
          <div style={{ padding:"14px 18px",borderRadius:12,background:"rgba(6,6,6,0.8)",border:"1px solid rgba(255,28,53,0.4)",boxShadow:"0 0 24px rgba(255,28,53,0.15),inset 0 0 16px rgba(255,28,53,0.05)" }}>
            <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:5 }}><div className="pdot"/><span style={{ fontSize:9,color:"rgba(255,255,255,0.35)",letterSpacing:2.5,fontWeight:700 }}>LIVE</span></div>
            <div style={{ fontSize:24,fontWeight:900,color:"#FF1C35",fontFamily:"JetBrains Mono,monospace",lineHeight:1 }}>500K+</div>
            <div style={{ fontSize:9,color:"rgba(255,255,255,0.4)",letterSpacing:2,marginTop:4 }}>SEA USERS</div>
            <div className="dline" style={{ marginTop:10,animationDelay:"0.5s" }}/>
          </div>
        </div>
        {/* Right card */}
        <div className="fcard" style={{ position:"absolute",right:"4.5%",top:"34%",animationDuration:"6.5s",animationDelay:"1.2s" }}>
          <div style={{ padding:"14px 18px",borderRadius:12,background:"rgba(6,6,6,0.8)",border:"1px solid rgba(255,28,53,0.4)",boxShadow:"0 0 24px rgba(255,28,53,0.15),inset 0 0 16px rgba(255,28,53,0.05)" }}>
            <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:5 }}><div className="pdot" style={{ animationDelay:"0.7s" }}/><span style={{ fontSize:9,color:"rgba(255,255,255,0.35)",letterSpacing:2.5,fontWeight:700 }}>REVENUE</span></div>
            <div style={{ fontSize:24,fontWeight:900,color:"#FF1C35",fontFamily:"JetBrains Mono,monospace",lineHeight:1 }}>RM 16M+</div>
            <div style={{ fontSize:9,color:"rgba(255,255,255,0.4)",letterSpacing:2,marginTop:4 }}>TOTAL GENERATED</div>
            <div className="dline" style={{ marginTop:10,animationDelay:"1.5s" }}/>
          </div>
        </div>
        {/* Bottom-left mini */}
        <div className="fcard" style={{ position:"absolute",left:"6%",bottom:"19%",animationDuration:"7s",animationDelay:"2s" }}>
          <div style={{ padding:"10px 14px",borderRadius:10,background:"rgba(6,6,6,0.7)",border:"1px solid rgba(255,255,255,0.1)" }}>
            <div style={{ fontSize:9,color:"rgba(255,255,255,0.3)",letterSpacing:1.5 }}>CYCLE</div>
            <div style={{ fontSize:16,fontWeight:800,color:"#fff",fontFamily:"JetBrains Mono,monospace" }}>7–14 <span style={{ fontSize:10,opacity:0.4 }}>DAYS</span></div>
          </div>
        </div>
      </div>

      {/* Top/Bottom fades */}
      <div style={{ position:"absolute",top:0,left:0,right:0,height:"14%",zIndex:9,background:"linear-gradient(to bottom,#080808,transparent)",pointerEvents:"none" }}/>
      <div style={{ position:"absolute",bottom:0,left:0,right:0,height:"22%",zIndex:9,background:"linear-gradient(to top,#080808,transparent)",pointerEvents:"none" }}/>

      {/* CONTENT */}
      <div style={{ position:"relative",zIndex:10,textAlign:"center",maxWidth:920 }}>
        <motion.div initial={{ opacity:0,y:18 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.2,duration:0.7 }}>
          <span style={{ display:"inline-flex",alignItems:"center",gap:8,padding:"7px 20px",border:"1px solid rgba(255,28,53,0.5)",borderRadius:999,fontSize:11,fontWeight:700,color:"#FF1C35",letterSpacing:2.5,marginBottom:32,background:"rgba(255,28,53,0.08)",boxShadow:"0 0 24px rgba(255,28,53,0.18)" }}>
            <span style={{ width:6,height:6,borderRadius:"50%",background:"#FF1C35",display:"inline-block",animation:"pulseDot 1.5s infinite" }}/>
            AI-POWERED PAN-ENTERTAINMENT PLATFORM · KUALA LUMPUR
          </span>
        </motion.div>

        <motion.div initial={{ opacity:0,y:30 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.35,duration:0.9 }}>
          <h1 style={{ fontSize:"clamp(42px,8.5vw,92px)",fontWeight:900,lineHeight:1.03,margin:"0 0 6px",letterSpacing:"-2px" }}>
            <span style={{ color:"#fff",display:"block" }}>NEXT</span>
            <span className="neon" style={{ color:"#FF1C35",display:"block",position:"relative" }}>
              <span className="glitch-wrap" data-text="GENERATION">GENERATION</span>
            </span>
            <span style={{ color:"#fff",display:"block" }}>AI ENTERTAINMENT</span>
            <span style={{ display:"block",color:"transparent",WebkitTextStroke:"1.5px rgba(255,255,255,0.16)",letterSpacing:"-1px" }}>PLATFORM</span>
          </h1>
        </motion.div>

        <motion.p initial={{ opacity:0,y:16 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.55,duration:0.7 }}
          style={{ fontSize:17,color:"rgba(255,255,255,0.5)",maxWidth:520,margin:"22px auto 6px",lineHeight:1.75 }}>
          Southeast Asia's leading short drama production & distribution ecosystem.<br/>
          <span style={{ fontSize:13,color:"rgba(255,255,255,0.22)" }}>东南亚领先的AI短剧制作与分发平台</span>
        </motion.p>

        <motion.div initial={{ opacity:0,y:16 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.7,duration:0.7 }}
          style={{ display:"flex",gap:16,justifyContent:"center",flexWrap:"wrap",marginTop:44 }}>
          <button onClick={() => scrollTo("#drama")} style={{ padding:"15px 40px",background:"#FF1C35",color:"#fff",border:"none",borderRadius:8,fontSize:15,fontWeight:700,cursor:"pointer",boxShadow:"0 0 40px rgba(255,28,53,0.55),0 0 90px rgba(255,28,53,0.22)",transition:"transform 0.2s,box-shadow 0.2s" }}
            onMouseEnter={e=>{ e.currentTarget.style.transform="scale(1.06)"; e.currentTarget.style.boxShadow="0 0 64px rgba(255,28,53,0.75),0 0 130px rgba(255,28,53,0.35)"; }}
            onMouseLeave={e=>{ e.currentTarget.style.transform="scale(1)"; e.currentTarget.style.boxShadow="0 0 40px rgba(255,28,53,0.55),0 0 90px rgba(255,28,53,0.22)"; }}>
            Explore MIRE DRAMA →
          </button>
          <button onClick={() => scrollTo("#partnership")} style={{ padding:"15px 40px",background:"transparent",color:"#fff",border:"1px solid rgba(255,255,255,0.2)",borderRadius:8,fontSize:15,fontWeight:600,cursor:"pointer",transition:"all 0.2s" }}
            onMouseEnter={e=>{ e.currentTarget.style.borderColor="#FF1C35"; e.currentTarget.style.color="#FF1C35"; e.currentTarget.style.boxShadow="0 0 20px rgba(255,28,53,0.25)"; }}
            onMouseLeave={e=>{ e.currentTarget.style.borderColor="rgba(255,255,255,0.2)"; e.currentTarget.style.color="#fff"; e.currentTarget.style.boxShadow="none"; }}>
            Partner With Us
          </button>
        </motion.div>

        <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:1.3,duration:0.6 }}
          style={{ position:"absolute",bottom:-106,left:"50%",transform:"translateX(-50%)" }}>
          <motion.div animate={{ y:[0,9,0] }} transition={{ repeat:Infinity,duration:1.8,ease:"easeInOut" }}
            style={{ color:"rgba(255,255,255,0.18)",fontSize:20 }}>↓</motion.div>
        </motion.div>
      </div>
    </section>
  );
};
export default Hero;
